/*───────────────────────────────────────────────────────────────
 📄 File: components/CdrFilterConditionsBox.tsx
 🧩 Description:
    Search Conditions FormBox with dynamic rows table (Condition,
    SearchBy, Value, Exclude, Mode, Delete). Includes Add Condition button.
────────────────────────────────────────────────────────────────*/

import { Plus, Trash2 } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../hooks/useI18n';
import { SearchConditionItem } from '../types';
import { HANGUP_CAUSE_VALUES } from '../constants/hangupCauses';
import { CALL_TYPE_VALUES } from '../constants/callTypes';

const SELECT_BASED_FIELDS = ['hangup_cause', 'call_type'] as const;
const SEARCH_BY_OPTIONS = ['caller_id', 'source', 'destination', 'did', 'account_code', 'customer_code', 'hangup_cause', 'call_type'] as const;
const MODE_OPTIONS = ['begins_with', 'contains', 'ends_with', 'exactly'] as const;
const SELECT_CLASS = 'w-full h-10 px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

interface Props {
    searchConditions: SearchConditionItem[];
    errors: Record<string, string>;
    onAdd: () => void;
    onRemove: (index: number) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (index: number, field: keyof SearchConditionItem, value: any) => void;
}

export function CdrFilterConditionsBox({ searchConditions, errors, onAdd, onRemove, onChange }: Props) {
    const { t } = useI18n();

    return (
        <FormBox
            title={t('cdrFilters.sections.searchConditions')}
            description={t('cdrFilters.description')}
        >
            <div className="space-y-4">
                <Label className="text-sm font-medium">
                    {t('cdrFilters.sections.searchConditions')} <span className="text-red-500">*</span>
                </Label>

                <div className="overflow-x-auto overflow-y-visible">
                    <table className="w-full min-w-[900px] table-auto border-spacing-y-2" style={{ position: 'relative' }}>
                        <thead>
                            <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                                <th className="px-2 py-2">{t('cdrFilters.fields.condition.label')}</th>
                                <th className="px-2 py-2">{t('cdrFilters.fields.searchBy.label')}</th>
                                <th className="px-2 py-2">{t('cdrFilters.fields.value.label')}</th>
                                <th className="px-2 py-2">{t('cdrFilters.fields.exclude.label')}</th>
                                <th className="px-2 py-2">{t('cdrFilters.fields.mode.label')}</th>
                                <th className="px-2 py-2 w-16" />
                            </tr>
                        </thead>
                        <tbody>
                            {searchConditions.map((cond, index) => {
                                const isSelectBased = SELECT_BASED_FIELDS.includes(cond.searchBy as typeof SELECT_BASED_FIELDS[number]);
                                return (
                                    <tr key={index} className="border-b border-border/60">
                                        {/* Condition (AND/OR) */}
                                        <td className="px-2 py-3">
                                            <select
                                                value={cond.condition}
                                                onChange={(e) => onChange(index, 'condition', e.target.value as 'AND' | 'OR')}
                                                className={SELECT_CLASS}
                                            >
                                                <option value="AND">{t('cdrFilters.options.condition.AND')}</option>
                                                <option value="OR">{t('cdrFilters.options.condition.OR')}</option>
                                            </select>
                                        </td>

                                        {/* Search By */}
                                        <td className="px-2 py-3">
                                            <select
                                                value={cond.searchBy}
                                                onChange={(e) => onChange(index, 'searchBy', e.target.value)}
                                                className={SELECT_CLASS}
                                            >
                                                {SEARCH_BY_OPTIONS.map((field) => (
                                                    <option key={field} value={field}>
                                                        {t(`cdrFilters.options.searchBy.${field}`)}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Value — select or text depending on searchBy */}
                                        <td className="px-2 py-3">
                                            {cond.searchBy === 'hangup_cause' ? (
                                                <select value={cond.value} onChange={(e) => onChange(index, 'value', e.target.value)} className={SELECT_CLASS}>
                                                    <option value="">{t('cdrFilters.fields.value.placeholder')}</option>
                                                    {HANGUP_CAUSE_VALUES.map((cause) => (
                                                        <option key={cause} value={cause}>
                                                            {t(`cdrFilters.options.hangupCauses.${cause}`, { defaultValue: cause })}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : cond.searchBy === 'call_type' ? (
                                                <select value={cond.value} onChange={(e) => onChange(index, 'value', e.target.value)} className={SELECT_CLASS}>
                                                    <option value="">{t('cdrFilters.fields.value.placeholder')}</option>
                                                    {CALL_TYPE_VALUES.map((ct) => (
                                                        <option key={ct} value={ct}>
                                                            {t(`cdrFilters.options.callTypes.${ct}`, { defaultValue: ct })}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <Input
                                                    value={cond.value}
                                                    onChange={(e) => onChange(index, 'value', e.target.value)}
                                                    placeholder={t('cdrFilters.fields.value.placeholder')}
                                                />
                                            )}
                                        </td>

                                        {/* Exclude */}
                                        <td className="px-2 py-3">
                                            <select
                                                value={cond.exclude ? 'yes' : 'no'}
                                                onChange={(e) => onChange(index, 'exclude', e.target.value === 'yes')}
                                                disabled={isSelectBased}
                                                className={SELECT_CLASS}
                                            >
                                                <option value="no">{t('cdrFilters.options.exclude.no')}</option>
                                                <option value="yes">{t('cdrFilters.options.exclude.yes')}</option>
                                            </select>
                                        </td>

                                        {/* Mode */}
                                        <td className="px-2 py-3">
                                            <select
                                                value={cond.mode}
                                                onChange={(e) => onChange(index, 'mode', e.target.value)}
                                                disabled={isSelectBased}
                                                className={SELECT_CLASS}
                                            >
                                                {MODE_OPTIONS.map((mode) => (
                                                    <option key={mode} value={mode}>
                                                        {t(`cdrFilters.options.mode.${mode}`)}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Remove */}
                                        <td className="px-2 py-3">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => onRemove(index)}
                                                disabled={searchConditions.length === 1}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
                                            >
                                                <Trash2 className="h-5 w-5" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {errors.searchConditions && <p className="text-sm text-red-500">{errors.searchConditions}</p>}

                <div className="flex justify-end">
                    <Button type="button" onClick={onAdd} variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        {t('cdrFilters.actions.addCondition')}
                    </Button>
                </div>
            </div>
        </FormBox>
    );
}
